import { FormEvent, useState } from "react";
import {
  Mail,
  Lock,
  Eye,
  EyeOff,
  ArrowRight,
} from "lucide-react";

import { useNavigate } from "react-router-dom";

import { useAuth } from "../context/AuthContext";
import { forgotPasswordApi, loginApi } from "../api/api";

export default function LoginPage() {
  const navigate = useNavigate();

  const { setPendingLogin } = useAuth();

  const [form, setForm] = useState({
    email: "",
    password: "",
  });

  const [showPass, setShowPass] =
    useState(false);

  const [error, setError] =
    useState("");

  const [loading, setLoading] =
    useState(false);
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotMessage, setForgotMessage] = useState("");

  const handleSubmit = async (
    e: FormEvent
  ) => {
    e.preventDefault();

    setError("");
    setForgotMessage("");

    if (
      !form.email 
    ) {
      setError(
        "Please enter your email"
      );

      return;
    }
    // Validate email format
    const emailRegex =
      /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(form.email)) {
        setError(
          "Please enter a valid email address"
        );
        return;
      }
    if (
      !form.password 
    ) {
      setError(
        "Please enter your password"
      );

      return;
    }

    try {
      setLoading(true);

      const data = await loginApi(
        form.email,
        form.password
      );

      setPendingLogin({
        email: data.user?.email || form.email,
        accessToken:
          data.access_token || "",
        expiresIn:
          data.expires_in || 3600,
        user: {
          id: data.user?.login_id || 0,
          name: data.user?.name || "User",
          email: data.user?.email || form.email,
          role:
            data.user?.user_type ||
            "retailer",
          token:
            data.access_token || "",
          expiresIn:
            data.expires_in || 3600,
        },
      });

      navigate("/otp");
    } catch (err: any) {
      setError(
        err.message ||
          "Login Failed"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!form.email) {
      setError("Please enter your email to receive reset instructions.");
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(form.email)) {
      setError("Please enter a valid email address.");
      return;
    }

    try {
      setForgotLoading(true);
      setError("");
      setForgotMessage("");
      await forgotPasswordApi(form.email);
      setForgotMessage("Password reset instructions have been sent to your email.");
    } catch (err: any) {
      setError(err.message || "Unable to send reset instructions");
    } finally {
      setForgotLoading(false);
    }
  };

  return (
    <div className="auth-page">

      <div className="auth-card">

        <div className="auth-logo">

          <div className="auth-logo-icon">
            💸
          </div>

          <h1>
            Money Transfer Portal
          </h1>

          <p>
            Welcome back! Please
            login to your account.
          </p>

        </div>

        <form
          onSubmit={handleSubmit}
        >

          <div className="form-group">

            <label className="form-label">
              Email
            </label>

            <div className="input-wrap">

              <Mail size={16} />

              <input
                type="text"
                className="form-input"
                placeholder="admin"
                value={form.email}
                onChange={(e) =>
                  setForm({
                    ...form,
                    email:
                      e.target.value,
                  })
                }
              />

            </div>

          </div>

          <div className="form-group">

            <label className="form-label">
              Password
            </label>

            <div
              className="input-wrap"
              style={{
                position:
                  "relative",
              }}
            >

              <Lock size={16} />

              <input
                type={
                  showPass
                    ? "text"
                    : "password"
                }
                className="form-input"
                placeholder="admin"
                value={form.password}
                onChange={(e) =>
                  setForm({
                    ...form,
                    password:
                      e.target.value,
                  })
                }
                style={{
                  paddingRight:
                    "42px",
                }}
              />

              <button
                type="button"
                onClick={() =>
                  setShowPass(
                    !showPass
                  )
                }
                style={{
                  position:
                    "absolute",
                  right: "12px",
                  top: "50%",
                  transform:
                    "translateY(-50%)",
                  background:
                    "none",
                  border: "none",
                  cursor:
                    "pointer",
                }}
              >

                {showPass ? (
                  <EyeOff
                    size={16}
                  />
                ) : (
                  <Eye size={16} />
                )}

              </button>

            </div>

          </div>

          {error && (
            <div className="error-msg">
              ⚠️ {error}
            </div>
          )}

          {forgotMessage && (
            <div className="success-msg" style={{ marginBottom: 12 }}>
              ✅ {forgotMessage}
            </div>
          )}

          <button
            type="submit"
            className="btn btn-primary btn-full"
            disabled={loading}
          >

            {loading ? (
              "Logging in..."
            ) : (
              <>
                Login
                <ArrowRight
                  size={16}
                />
              </>
            )}

          </button>

          <button
            type="button"
            className="btn btn-outline btn-full"
            onClick={handleForgotPassword}
            disabled={forgotLoading}
            style={{ marginTop: 8 }}
          >
            {forgotLoading ? "Sending..." : "Forgot Password"}
          </button>

        </form>


      </div>

    </div>
  );
}
